/**
 * useResellerNames (TMMPDM-0015) — ONE tmm-api `listResellers({ size: 200 })` per mount
 * (TMMPDM-0017; reads `content`), as the product's reseller list plus a `Map<id, name>`. Used
 * by the tenant roster (column and filter), the assign dialog and the tenant Overview to name
 * a `TenantView.resellerId`.
 * 200 is the server's page cap: a product with more resellers than that is out of scope,
 * and an id missing from the map renders as `#{{id}}` (still linked). A failed load keeps
 * the map empty, so a caller that only names an id still renders `#{{id}}`. `status` tells
 * a caller that must not treat that empty list as a complete partner list.
 */
import { useEffect, useState } from 'react';
import { adminApi } from '@/api/tmm/admin';
import type { ResellerDto } from '@/api/tmm/types';

export const RESELLER_NAMES_PAGE_SIZE = 200;

export interface UseResellerNames {
  resellers: ResellerDto[];
  names: Map<number, string>;
  status: 'loading' | 'ready' | 'failed';
  error: unknown;
  retry: () => void;
}

export function useResellerNames(): UseResellerNames {
  const [attempt, setAttempt] = useState(0);
  const [resellers, setResellers] = useState<ResellerDto[]>([]);
  const [names, setNames] = useState<Map<number, string>>(() => new Map());
  const [status, setStatus] = useState<UseResellerNames['status']>('loading');
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listResellers({ page: 0, size: RESELLER_NAMES_PAGE_SIZE })
      .then((page) => {
        if (cancelled) return;
        setResellers(page.content);
        setNames(new Map(page.content.map((r) => [r.id, r.name])));
        setError(null);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setResellers([]);
        setNames(new Map());
        setError(err);
        setStatus('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = () => {
    setStatus('loading');
    setError(null);
    setAttempt((n) => n + 1);
  };

  return { resellers, names, status, error, retry };
}

/** The display name for a reseller id — its name, else `#{{id}}`. */
export function resellerLabel(names: Map<number, string>, id: number): string {
  return names.get(id) ?? `#${id}`;
}

/**
 * The page-header scope for a reseller filter (TMMPDM-0035, PAGE-008): "All resellers" or
 * "Reseller: {{name}}".
 */
export function resellerScope(
  t: (key: string, fallback?: string, values?: Record<string, string | number>) => string,
  names: Map<number, string>,
  id: number | undefined,
): string {
  return id === undefined
    ? t('partners.scopeAll', 'All partners')
    : t('partners.scope', 'Partner: {{name}}', { name: resellerLabel(names, id) });
}

/**
 * TMMPDM-0044: a reseller filter's options — the product's resellers, plus a well-formed id from
 * the URL that the list doesn't hold, labelled "Reseller #{{id}}", so the control is never blank
 * (SubscriptionsPanel's `statusOptions` pattern).
 */
export function resellerFilterOptions(
  t: (key: string, fallback?: string, values?: Record<string, string | number>) => string,
  resellers: ResellerDto[],
  selected: number | undefined,
): Pick<ResellerDto, 'id' | 'name'>[] {
  return selected !== undefined && !resellers.some((reseller) => reseller.id === selected)
    ? [
        ...resellers,
        { id: selected, name: t('partners.number', 'Partner #{{id}}', { id: selected }) },
      ]
    : resellers;
}
